/**
 * Limits & Fees Articles
 */

import type { HelpArticle } from "../types";

export const LIMITS_FEES_ARTICLES: HelpArticle[] = [
  {
    id: "lf-001",
    slug: "transaction-limits-overview",
    title: "Transaction Limits Overview",
    description: "Understanding your account limits",
    category: "limits-fees",
    popular: true,
    order: 1,
    keywords: ["limits", "maximum", "minimum", "restrictions", "caps"],
    content: `
# Transaction Limits Overview

Your transaction limits depend on your verification level and account history.

## Limit Categories

### Deposit Limits
- Minimum: $10 per transaction
- Maximum: Varies by tier

### Payout Limits
- Per transaction limit
- Daily aggregate limit
- Monthly aggregate limit

### Conversion Limits
- Per conversion limit
- Daily conversion volume
- Monthly conversion volume

## Limits by Tier

### Standard Tier
| Limit Type | Amount |
|------------|--------|
| Per deposit | $25,000 |
| Per payout | $10,000 |
| Daily payouts | $25,000 |
| Monthly volume | $100,000 |

### Enhanced Tier
| Limit Type | Amount |
|------------|--------|
| Per deposit | $100,000 |
| Per payout | $50,000 |
| Daily payouts | $100,000 |
| Monthly volume | $500,000 |

### Enterprise Tier
| Limit Type | Amount |
|------------|--------|
| Per deposit | Custom |
| Per payout | Custom |
| Daily payouts | Custom |
| Monthly volume | Custom |

## Viewing Your Limits

1. Go to Settings > Limits
2. See current tier
3. View remaining limits
4. Track usage over time

## When Limits Reset

- **Daily limits**: Midnight UTC
- **Monthly limits**: First of each month
- **Per-transaction**: Immediate after completion

## Temporary Limits

Additional limits may apply for:
- New accounts (first 30 days)
- First-time large transactions
- After account changes
- Security reviews
    `.trim(),
  },
  {
    id: "lf-002",
    slug: "increasing-your-limits",
    title: "Increasing Your Limits",
    description: "How to qualify for higher limits",
    category: "limits-fees",
    order: 2,
    keywords: ["increase", "upgrade", "higher", "more", "expand"],
    content: `
# Increasing Your Limits

Ready for higher limits? Here's how to upgrade.

## Upgrade Paths

### Standard → Enhanced

**Requirements:**
- 30+ days account age
- $5,000+ successful volume
- No compliance issues
- Additional verification

**Steps:**
1. Go to Settings > Verification
2. Click "Upgrade to Enhanced"
3. Submit proof of address
4. Provide source of funds info
5. Wait for review (1-3 days)

### Enhanced → Enterprise

**Requirements:**
- Business account
- High transaction volume
- Dedicated account review
- Custom agreement

**Steps:**
1. Contact enterprise@supabank.xyz
2. Schedule consultation
3. Complete business verification
4. Negotiate terms
5. Account upgrade

## Required Documents

### For Enhanced
- Proof of address (recent utility bill)
- Bank statement (last 3 months)
- Source of funds declaration

### For Enterprise
- Business registration documents
- Financial statements
- Board resolution (if applicable)
- UBO verification

## Approval Timeline

| Upgrade | Typical Time |
|---------|--------------|
| Standard → Enhanced | 1-3 business days |
| Enhanced → Enterprise | 5-10 business days |

## Tips for Approval

Keep documents clear and readable
Ensure names match
Use recent documents (< 3 months)
Complete all fields accurately
Respond promptly to requests
    `.trim(),
  },
  {
    id: "lf-003",
    slug: "fee-structure",
    title: "Fee Structure",
    description: "Complete breakdown of all fees",
    category: "limits-fees",
    order: 3,
    keywords: ["fees", "cost", "pricing", "charges", "rates"],
    content: `
# Fee Structure

Transparent pricing with no hidden fees.

## Conversion Fees

When crypto converts to fiat, two fees apply:

1. **Platform fee**: 2.0% of the conversion amount
2. **Bridge conversion fee**: ~0.1% of the conversion amount

**Total conversion fee: ~2.1%**

### Fee Calculation Example

\`\`\`
Deposit: 1,000 USDC
├── Platform fee: $20.00 (2.0%)
├── Bridge fee: ~$1.00 (0.1%)
└── Total fees: ~$21.00

Net received: ~$979.00
\`\`\`

### Volume-Based Pricing

| Monthly Volume | Platform Fee | Total Fee (approx) |
|----------------|--------------|-------------------|
| Standard | 2.0% | ~2.1% |
| High Volume | 2.0% | ~2.1% |
| Enterprise ($1M+) | Custom | Custom |

*Bridge fee may vary slightly based on market conditions*

## Payout Fees

| Method | Fee |
|--------|-----|
| ACH (US) | Free |
| Wire (US Domestic) | $15 |
| Wire (International) | $25 |
| SEPA (Europe) | Free |
| SPEI (Mexico) | Free |

## No Hidden Fees

**We don't charge for:**
- Account creation
- Account maintenance
- Deposit reception
- Currency display
- Customer support
- API access (business)

## Third-Party Fees

**Your costs outside Supabank:**
- Blockchain network fees (gas)
- Your bank's incoming transfer fee
- Intermediary bank fees (international wires)
- Currency conversion at your bank

## Fee Calculation Example

\`\`\`
Deposit: 1,000 USDC
└── Network fee: ~$2 (paid by you to blockchain)

Conversion: 1,000 USDC → ~979 USD
└── Platform fee: $20.00 (2%)
└── Bridge fee: ~$1.00 (0.1%)
└── Total conversion fee: ~$21.00

Payout: ~$979 via ACH
└── Payout fee: $0 (free ACH)

═══════════════════════
Net Received: ~$979.00
\`\`\`

## Viewing Your Fees

1. Transaction details show all fees
2. Monthly summary in Settings
3. Export includes fee breakdown
    `.trim(),
  },
  {
    id: "lf-004",
    slug: "volume-based-pricing",
    title: "Volume-Based Pricing",
    description: "Discounts for high-volume users",
    category: "limits-fees",
    order: 4,
    keywords: ["volume", "discount", "pricing", "tier", "savings"],
    content: `
# Volume-Based Pricing

Higher volume means lower fees.

## Volume Tiers

| Monthly Volume | Platform Fee | Total Fee (approx) |
|----------------|--------------|-------------------|
| Up to $100,000 | 2.0% | ~2.1% |
| $100,001 - $500,000 | 2.0% | ~2.1% |
| $500,001 - $1,000,000 | 2.0% | ~2.1% |
| $1,000,000+ | Custom | Custom |

*Note: Platform fee is fixed at 2%. Bridge fee (~0.1%) is separate and may vary.*

## How Volume Is Calculated

- Rolling 30-day period
- All conversions included
- Updated daily
- Automatic tier assignment

## Tier Benefits

### Standard (< $100K)
- 2.0% platform fee (~2.1% total)
- Standard limits
- Email support
- Regular processing

### High Volume ($100K - $1M)
- 2.0% platform fee (~2.1% total)
- Higher limits
- Priority support
- Faster processing

### Enterprise ($1M+)
- Custom conversion rates
- Unlimited limits
- Dedicated manager
- White-glove service
- API priority

## Reaching Higher Tiers

1. Increase transaction volume
2. Maintain consistent activity
3. No compliance issues
4. Automatic upgrade

## Volume Tracking

View your volume:
1. Go to Settings > Limits
2. See "30-Day Volume"
3. View tier progress
4. Estimated tier change

## Enterprise Pricing

For volumes over $1M/month:
- Contact enterprise@supabank.xyz
- Custom rate negotiation
- Volume commitments
- SLA agreements
    `.trim(),
  },
  {
    id: "lf-005",
    slug: "enterprise-limits",
    title: "Enterprise Limits",
    description: "High-volume business account limits",
    category: "limits-fees",
    order: 5,
    keywords: ["enterprise", "business", "corporate", "unlimited", "custom"],
    content: `
# Enterprise Limits

Custom limits for high-volume business needs.

## Enterprise Features

### Custom Limits
- Transaction limits tailored to needs
- No standard caps
- Volume-based adjustments
- Flexible terms

### Dedicated Support
- Named account manager
- Direct phone access
- Priority ticket handling
- Technical integration support

### Enhanced SLA
- 99.9% uptime guarantee
- Priority processing
- Faster settlements
- Incident response times

## Qualifying for Enterprise

### Minimum Requirements
- Monthly volume: $1,000,000+
- Business verification complete
- Clean compliance history
- Stable transaction patterns

### Application Process
1. Contact enterprise@supabank.xyz
2. Initial consultation call
3. Business documentation review
4. Risk assessment
5. Custom agreement negotiation
6. Account upgrade

## Enterprise Pricing

| Service | Enterprise Rate |
|---------|-----------------|
| Conversion | As low as 0.15% |
| Wire payouts | Reduced or waived |
| API calls | Unlimited |
| Support | Premium included |

*Final rates depend on volume and terms*

## Integration Options

### API Access
- Full API access
- Sandbox environment
- Technical documentation
- Integration support

### Custom Solutions
- White-label options
- Custom reporting
- Dedicated endpoints
- Bulk operations

## Getting Started

Ready for enterprise? Contact:
- Email: enterprise@supabank.xyz
- Include: Company name, expected volume, use case
    `.trim(),
  },
];
